/**
 * Wordtune — AI paraphrasing / grammar assistant
 *
 * Declares three WAR entries and no use_dynamic_url: one exposes `content-scripts/*.js`
 * (and asset globs) to every origin, the other two expose `content.css` and `read.css`
 * to <all_urls>. The content scripts are vendor-named and reachable at a fixed URL from
 * any page — `content-scripts/wordtuneExtensionInstalled.js` is uniquely Wordtune's, so
 * a single fetch is conclusive.
 *
 * Manifest verified against version 9.20.0.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'nllcnknpjnininklegdoijpljgdjkijc';

export const wordtune: ExtensionDefinition = {
  id: ID,
  name: 'Wordtune: AI Paraphrasing and Grammar Tool',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 1_000_000,
  notes:
    'content-scripts/*.js exposed to every origin with no dynamic URL, plus two CSS ' +
    'files on <all_urls>. The wordtuneExtensionInstalled.js path is conclusive.',
  signals: [
    {
      id: 'war-wordtune-installed-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch content-scripts/wordtuneExtensionInstalled.js — a vendor-named content ' +
        'script exposed to every origin, distinctive to Wordtune.',
      weight: 1,
      run: () => probeFetch(ID, 'content-scripts/wordtuneExtensionInstalled.js'),
    },
    {
      id: 'war-browser-detector-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch content-scripts/browserDetector.js — a second content script, corroborating ' +
        'without relying on the first path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'content-scripts/browserDetector.js'),
    },
    {
      id: 'war-content-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch content-scripts/content.css — a stylesheet exposed to <all_urls> in a ' +
        'separate WAR entry, independent of the JS paths.',
      weight: 0.5,
      run: () => probeFetch(ID, 'content-scripts/content.css'),
    },
  ],
};
